/* eslint-disable react/jsx-props-no-spreading */
import React, { ChangeEvent, useEffect, useMemo } from "react";
import { addMinutes, differenceInMinutes } from "date-fns";
import { Content } from "@iventis/translations/content/typed-content";
import { StyledFieldLabel } from "@iventis/styles/src/components/forms";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { convertTimeStringToNum } from "@iventis/utilities";
import { NumberOnlyInput } from "@iventis/components/src/number-only-input";
import { splitTimeToSingle } from "../schedule-helper-functions";
import { SetFieldValue } from "../schedule.types";

interface DurationInputProps {
    value: number;
    setFieldValue: SetFieldValue<{ duration: number; endTime: string }>;
    startTime: string;
    endTime: string;
    label?: string;
    className?: string;
}
const DurationInput: React.FunctionComponent<DurationInputProps> = ({ value, setFieldValue, startTime, endTime, label, className }) => {
    const translate = useIventisTranslate();

    const duration = useMemo(() => (endTime == null ? null : computeDurationSingleString(endTime, startTime)), [startTime, endTime]);

    useEffect(() => {
        setFieldValue("duration", duration);
    }, [duration]);

    const handleOnDurationChange = (event: ChangeEvent<HTMLInputElement>) => {
        const durationInputValue = parseInt(event.target.value || "0", 10);
        const endTime = calculateEndTime(startTime, durationInputValue);
        const endTimeDate = splitTimeToSingle(endTime.endHours, endTime.endMins, endTime.endSeconds);
        setFieldValue("endTime", endTimeDate);
    };

    return (
        <div className={className}>
            <StyledFieldLabel>{label || <span>{translate(Content.schedule.create_session_form.inputLabels.duration)}</span>}</StyledFieldLabel>
            <NumberOnlyInput variant="outlined" name="duration" value={value || ""} onChange={handleOnDurationChange} className="duration-textfield" type="number" />
        </div>
    );
};

// This function calculates the difference in minutes between 2 sets of times comprised of HH:mm:ss
const computeDurationSingleString = (endTime, startTime) => {
    const endTimeSplit = endTime.split(":");
    const startTimeSplit = startTime.split(":");
    // the next one-liner creates 2 Date objects with the same year/day/month BUT with the different end/start hours/minutes provided
    // and feeds them to the differenceInMinutes function
    let difference = differenceInMinutes(new Date(2000, 0, 1, endTimeSplit[0], endTimeSplit[1]), new Date(2000, 0, 1, startTimeSplit[0], startTimeSplit[1]));
    // avoids returning NaN and negative results
    if (!Number.isNaN(difference) && difference > 0) {
        return difference;
    }
    difference = 0;
    return difference;
};

export default DurationInput;

export const calculateEndTime = (startTime: string, duration: number): { endHours: number; endMins: number; endSeconds: number } => {
    // Convert params to integers
    const startTimeSplit = convertTimeStringToNum(startTime);
    // Create start time
    const startDate = new Date(2000, 0, 1, startTimeSplit[0], startTimeSplit[1]);
    // Create an end time by adding the minutes
    const endTime = addMinutes(startDate, duration);
    // Needs to be removed when we can handle sessions across multiple days
    // If end extends to the next day or beyond then max to end of this day
    if (endTime >= new Date(2000, 0, 2)) {
        return { endHours: 23, endMins: 59, endSeconds: 59 };
    }
    return { endHours: endTime.getHours(), endMins: endTime.getMinutes(), endSeconds: endTime.getSeconds() };
};
